import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar, ShieldCheck } from "lucide-react";
import { findPost, posts } from "@/data/blog";

export default function BlogArticle() {
  const { slug } = useParams();
  const post = findPost(slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} — Go Higher`;
    setMeta("description", post.excerpt);
    setMeta("og:title", post.title, true);
    setMeta("og:description", post.excerpt, true);
    setMeta("og:type", "article", true);
    setMeta("og:image", post.hero_image, true);
    // JSON-LD Article schema for search engines
    let ld = document.getElementById("jsonld-article");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "jsonld-article";
      document.head.appendChild(ld);
    }
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Organization", name: "Go Higher" },
      image: [post.hero_image],
      description: post.excerpt,
      publisher: {
        "@type": "Organization",
        name: "Go Higher",
        logo: {
          "@type": "ImageObject",
          url: "https://igohigher.com/logo.png",
        },
      },
    });
    return () => {
      const stale = document.getElementById("jsonld-article");
      if (stale) stale.remove();
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#0A0F1C]/85 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/blog"
            data-testid="article-back-blog"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All posts
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">
              Go <span className="text-[#D4AF37]">Higher</span>
            </span>
          </div>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
          {post.category}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3 leading-[1.1] tracking-tight">
          {post.title}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/50">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {post.read_min} min read
          </span>
        </div>

        <img
          src={post.hero_image}
          alt={post.title}
          className="mt-10 w-full aspect-[16/8] rounded-2xl object-cover border border-white/10"
          loading="eager"
        />

        <div
          className="blog-content mt-12"
          dangerouslySetInnerHTML={{ __html: post.body_html }}
        />

        <div className="mt-16 rounded-2xl border border-[#10B981]/20 bg-gradient-to-br from-[#10B981]/[0.05] to-transparent p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
            Free audit — no obligation
          </div>
          <h2 className="font-display text-2xl font-bold mt-2 leading-tight">
            Find out which of your reviews are actually removable.
          </h2>
          <Link
            to="/#audit"
            className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white font-semibold text-sm shadow-lg shadow-emerald-500/25 transition-colors"
          >
            Request my free audit
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold">
              Related reading
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group rounded-xl border border-white/10 hover:border-white/25 bg-white/[0.02] p-5 transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">
                    {p.category}
                  </div>
                  <div className="font-display font-bold text-lg mt-1.5 leading-snug group-hover:text-[#D4AF37] transition-colors">
                    {p.title}
                  </div>
                  <div className="mt-2 text-xs text-white/50 line-clamp-2">
                    {p.excerpt}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/40 flex items-center justify-between flex-wrap gap-3">
          <div>© {new Date().getFullYear()} Go Higher, Inc.</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function setMeta(name, value, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}
